import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import Select from "react-select";
import "./ApplyForm.css";

function ApplyForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    course: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const courseOptions = [
    { value: "Playgroup", label: "Playgroup" },
    { value: "Nursery", label: "Nursery" },
    { value: "Prep", label: "Prep" },
    { value: "One", label: "One" },
    { value: "Two", label: "Two" },
    { value: "Three", label: "Three" },
    { value: "Four", label: "Four" },
    { value: "Five", label: "Five" },
    { value: "Six", label: "Six" },
    { value: "Seven", label: "Seven" },
    { value: "Eight", label: "Eight" },
    { value: "Nine", label: "Nine" },
    { value: "Tenth", label: "Ten" },
    { value: "1st Year", label: "1st Year" },
    { value: "2nd Year", label: "2nd Year" },
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email.trim())
    ) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^03\d{9}$/.test(formData.phone.trim())) {
      newErrors.phone = "Phone must be in format 03XXXXXXXXX (11 digits)";
    }

    if (!formData.course) {
      newErrors.course = "Please select a course";
    }

    return newErrors;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCourseChange = (selectedOption) => {
    setFormData({
      ...formData,
      course: selectedOption ? selectedOption.value : "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setStatusMessage("");
    setLoading(true);

    const formErrors = validate();
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      try {
        await addDoc(collection(db, "admissions"), {
          ...formData,
          createdAt: serverTimestamp(),
        });

        setStatusMessage(
          "✅ Your application has been submitted successfully! Our team will contact you shortly."
        );
        setFormData({ name: "", email: "", phone: "", course: "", message: "" });
        setErrors({});
        setSubmitted(false);

        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error("Error adding document: ", error);
        setStatusMessage("❌ Failed to submit application. Please try again.");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      setStatusMessage("⚠️ Please fix the errors above and try again.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    setLoading(false);
  };

  return (
    <div className="apply-form-page">
      <h2>Admission Application</h2>
      {statusMessage && (
        <p
          className={`status-message ${
            statusMessage.startsWith("✅") ? "success" : "error"
          }`}
        >
          {statusMessage}
        </p>
      )}
      <form
        onSubmit={handleSubmit}
        className="apply-form"
        noValidate
        autoComplete="off" // ✅ prevent browser autofill
      >
        <input
          type="text"
          name="name"
          placeholder="Full Name"
          value={formData.name}
          onChange={handleChange}
          aria-invalid={errors.name ? "true" : "false"}
          aria-describedby="name-error"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          required
        />
        {submitted && errors.name && (
          <span className="error" id="name-error" role="alert">
            {errors.name}
          </span>
        )}

        <input
          type="email"
          name="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={handleChange}
          aria-invalid={errors.email ? "true" : "false"}
          aria-describedby="email-error"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          required
        />
        {submitted && errors.email && (
          <span className="error" id="email-error" role="alert">
            {errors.email}
          </span>
        )}

        <input
          type="tel"
          name="phone"
          placeholder="Phone Number (03XXXXXXXXX)"
          value={formData.phone}
          onChange={handleChange}
          aria-invalid={errors.phone ? "true" : "false"}
          aria-describedby="phone-error"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          required
        />
        {submitted && errors.phone && (
          <span className="error" id="phone-error" role="alert">
            {errors.phone}
          </span>
        )}

        <textarea
          name="message"
          placeholder="Message (optional)"
          value={formData.message}
          onChange={handleChange}
          rows="3"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />

        <Select
          options={courseOptions}
          placeholder="Select Class..."
          value={
            courseOptions.find((opt) => opt.value === formData.course) || null
          }
          onChange={handleCourseChange}
          isClearable
          isSearchable
          classNamePrefix="react-select"
          aria-label="Course selection"
          aria-invalid={errors.course ? "true" : "false"}
        />
        {submitted && errors.course && (
          <span
            className="error"
            role="alert"
            style={{ display: "block", marginTop: "4px" }}
          >
            {errors.course}
          </span>
        )}

        <button type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Submit Application"}
        </button>
      </form>
    </div>
  );
}

export default ApplyForm;
